"use client";

import { useLocale } from "next-intl";
import { usePathname } from "@/i18n/navigation";
import { routing } from "@/i18n/routing";
import { useAuthStore } from "@/lib/stores/auth-store";
import { api } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Display names in each locale's own script. Keys match frontend/src/i18n/routing.ts.
const LOCALE_LABELS: Record<string, string> = {
  en: "English",
  ar: "العربية",
};

export function LocaleSwitcher() {
  const currentLocale = useLocale();
  const pathname = usePathname();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // Admin is English-only by design (see .claude/rules/i18n.md). The switcher is
  // irrelevant there, and clicking Arabic would navigate to /ar/admin/* which
  // does not exist as a route (admin lives outside app/[locale]/* and the
  // middleware excludes /admin/*). Hide the switcher on admin paths entirely.
  if (pathname.startsWith("/admin")) {
    return null;
  }

  function handleChange(newLocale: string) {
    if (newLocale === currentLocale) return;
    // For authenticated users, also persist in the DB (fire-and-forget).
    // The backend call is the cross-device sync convenience per spec Section 5b.
    if (isAuthenticated) {
      api.user.updateLocale(newLocale).catch((err) => {
        // Non-blocking: the switch succeeds at the URL level regardless.
        console.warn("Failed to persist locale preference:", err);
      });
    }
    // Write the locale cookie BEFORE navigating. If the user is switching
    // from 'ar' back to the default locale, the stale cookie would make the
    // proxy redirect the bare URL back to /ar. Setting the cookie first
    // keeps the proxy in sync with the user's new choice.
    const cookieConfig = routing.localeCookie;
    if (cookieConfig && typeof cookieConfig === "object") {
      const { name, maxAge = 60 * 60 * 24 * 365, sameSite = "lax" } = cookieConfig;
      const secure = window.location.protocol === "https:";
      document.cookie =
        `${name}=${newLocale}; path=/; max-age=${maxAge}; samesite=${sameSite}` +
        (secure ? "; secure" : "");
    }
    // Hard navigation: a soft `router.replace` only re-renders the [locale]
    // segment, leaving the root layout's <html lang> and <html dir> stale.
    // Users would see Arabic copy in an LTR document with English fonts until
    // the next full reload. Hard-nav forces the root layout to re-run.
    const isDefault = newLocale === routing.defaultLocale;
    const target = isDefault
      ? pathname
      : `/${newLocale}${pathname === "/" ? "" : pathname}`;
    window.location.assign(target);
  }

  return (
    <Select value={currentLocale} onValueChange={handleChange}>
      <SelectTrigger className="w-auto gap-2 border-none bg-transparent px-2 text-sm shadow-none focus:ring-0">
        <SelectValue>{LOCALE_LABELS[currentLocale] ?? currentLocale}</SelectValue>
      </SelectTrigger>
      <SelectContent align="end">
        {Object.entries(LOCALE_LABELS).map(([code, label]) => (
          <SelectItem key={code} value={code}>
            {label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
